<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
    data: { type: Object, required: true },
});

const BRAND = '#7814D7'; // myflow purple
const GOAL_GRAY = '#d1d5db';

const months = computed(() => props.data.months);
const people = computed(() => props.data.people);

const selected = ref(props.data.months[0].key);
const currentMonth = computed(() => months.value.find((m) => m.key === selected.value));

function val(person, month, metric) {
    return person.months?.[month]?.[metric] ?? 0;
}

// per-person rows for the selected month, sorted desc by impressions
const rows = computed(() =>
    people.value
        .map((p) => ({
            name: p.name,
            impressions: val(p, selected.value, 'impressions'),
            registrations: val(p, selected.value, 'registrations'),
        }))
        .sort((a, b) => b.impressions - a.impressions)
);

const maxImpressions = computed(() => Math.max(1, ...rows.value.map((r) => r.impressions)));
const maxRegistrations = computed(() => Math.max(1, ...rows.value.map((r) => r.registrations)));

const totalImpressions = computed(() => rows.value.reduce((s, r) => s + r.impressions, 0));
const totalRegistrations = computed(() => rows.value.reduce((s, r) => s + r.registrations, 0));

const pct = (actual, goal) => (goal > 0 ? Math.round((actual / goal) * 100) : 0);
// impression -> registration conversion rate, one decimal
const conv = (registrations, impressions) => (impressions > 0 ? (registrations / impressions * 100).toFixed(1) : '0.0');
const fmt = (n) => n.toLocaleString('en-US');

// --- monthly trend: actual vs goal (two separate charts, never dual-axis) ---
function trendOptions(unitLabel) {
    return {
        chart: { fontFamily: 'inherit', background: 'transparent', type: 'bar', toolbar: { show: false } },
        colors: [BRAND, GOAL_GRAY],
        plotOptions: { bar: { columnWidth: '58%', borderRadius: 4, borderRadiusApplication: 'end' } },
        dataLabels: { enabled: false },
        legend: { position: 'top', horizontalAlign: 'left', fontWeight: 600, labels: { colors: '#374151' }, markers: { radius: 3 } },
        grid: { borderColor: '#f3f4f6', xaxis: { lines: { show: false } } },
        xaxis: {
            categories: months.value.map((m) => m.key.slice(0, 3)),
            labels: { style: { colors: '#6b7280', fontWeight: 600 } },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: '#9ca3af' }, formatter: fmt } },
        tooltip: { theme: 'light', y: { formatter: (v) => `${fmt(v)} ${unitLabel}` } },
    };
}

const impressionsTrendOptions = computed(() => trendOptions('impressions'));
const impressionsTrendSeries = computed(() => [
    { name: 'Actual', data: months.value.map((m) => people.value.reduce((s, p) => s + val(p, m.key, 'impressions'), 0)) },
    { name: 'Goal', data: months.value.map((m) => m.impressionsGoal) },
]);

const companiesTrendOptions = computed(() => trendOptions('companies'));
const companiesTrendSeries = computed(() => [
    { name: 'Actual', data: months.value.map((m) => people.value.reduce((s, p) => s + val(p, m.key, 'registrations'), 0)) },
    { name: 'Goal', data: months.value.map((m) => m.companiesGoal) },
]);
</script>

<template>
    <div class="p-4 md:p-6">
        <div class="flex flex-col gap-1 mb-5">
            <h1 class="text-2xl font-bold text-gray-900">Marketing reach &amp; registrations</h1>
            <p class="text-sm text-gray-500">Impressions and new company registrations by team member.</p>
        </div>

        <!-- month tabs -->
        <div class="flex flex-wrap gap-2 mb-6">
            <button
                v-for="m in months"
                :key="m.key"
                @click="selected = m.key"
                class="px-4 py-2 text-sm font-semibold transition-colors rounded-lg border"
                :class="selected === m.key
                    ? 'bg-brandPrimary text-white border-brandPrimary'
                    : 'bg-white text-gray-500 border-gray-200 hover:text-gray-800'"
            >
                {{ m.key }}
            </button>
        </div>

        <!-- goal cards -->
        <div class="grid gap-4 mb-4 sm:grid-cols-3">
            <div class="p-6 bg-white rounded-lg">
                <div class="flex items-baseline justify-between">
                    <span class="text-sm font-medium text-gray-600">Impressions</span>
                    <span class="text-xs font-semibold"
                          :class="pct(totalImpressions, currentMonth.impressionsGoal) >= 100 ? 'text-myflowGreen' : 'text-gray-400'">
                        {{ pct(totalImpressions, currentMonth.impressionsGoal) }}% of goal
                    </span>
                </div>
                <div class="mt-2 text-3xl font-bold tracking-tight text-brandPrimary">{{ fmt(totalImpressions) }}</div>
                <div class="text-sm text-gray-500">Goal {{ fmt(currentMonth.impressionsGoal) }}</div>
                <div class="mt-3 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                    <div class="h-full rounded-full transition-all duration-500"
                         :class="pct(totalImpressions, currentMonth.impressionsGoal) >= 100 ? 'bg-myflowGreen' : 'bg-brandPrimary'"
                         :style="{ width: Math.min(100, pct(totalImpressions, currentMonth.impressionsGoal)) + '%' }"></div>
                </div>
            </div>

            <div class="p-6 bg-white rounded-lg">
                <div class="flex items-baseline justify-between">
                    <span class="text-sm font-medium text-gray-600">New companies</span>
                    <span class="text-xs font-semibold"
                          :class="pct(totalRegistrations, currentMonth.companiesGoal) >= 100 ? 'text-myflowGreen' : 'text-gray-400'">
                        {{ pct(totalRegistrations, currentMonth.companiesGoal) }}% of goal
                    </span>
                </div>
                <div class="mt-2 text-3xl font-bold tracking-tight text-brandPrimary">{{ fmt(totalRegistrations) }}</div>
                <div class="text-sm text-gray-500">Goal {{ fmt(currentMonth.companiesGoal) }}</div>
                <div class="mt-3 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                    <div class="h-full rounded-full transition-all duration-500"
                         :class="pct(totalRegistrations, currentMonth.companiesGoal) >= 100 ? 'bg-myflowGreen' : 'bg-brandPrimary'"
                         :style="{ width: Math.min(100, pct(totalRegistrations, currentMonth.companiesGoal)) + '%' }"></div>
                </div>
            </div>

            <div class="p-6 bg-white rounded-lg">
                <div class="flex items-baseline justify-between">
                    <span class="text-sm font-medium text-gray-600">Impression → registration</span>
                </div>
                <div class="mt-2 text-3xl font-bold tracking-tight text-brandPrimary">{{ conv(totalRegistrations, totalImpressions) }}%</div>
                <div class="text-sm text-gray-500">{{ fmt(totalRegistrations) }} of {{ fmt(totalImpressions) }} impressions</div>
            </div>
        </div>

        <!-- per-person breakdown (HTML bars — readable, on-brand) -->
        <div class="grid gap-4 mb-4 lg:grid-cols-2">
            <div class="p-6 bg-white rounded-lg">
                <h2 class="mb-5 text-xl font-semibold text-gray-800">Impressions by person — {{ selected }}</h2>
                <div v-for="row in rows" :key="row.name" class="mb-4 last:mb-0">
                    <div class="flex items-baseline justify-between mb-1">
                        <span class="text-sm font-medium text-gray-700">{{ row.name }}</span>
                        <span class="text-sm font-bold text-gray-900">{{ fmt(row.impressions) }}</span>
                    </div>
                    <div class="h-3 rounded-full bg-gray-100 overflow-hidden">
                        <div class="h-full rounded-full bg-brandPrimary transition-all duration-500"
                             :style="{ width: (row.impressions / maxImpressions * 100) + '%' }"></div>
                    </div>
                </div>
            </div>

            <div class="p-6 bg-white rounded-lg">
                <h2 class="mb-5 text-xl font-semibold text-gray-800">New companies by person — {{ selected }}</h2>
                <div v-for="row in rows" :key="row.name" class="mb-4 last:mb-0">
                    <div class="flex items-baseline justify-between mb-1">
                        <span class="text-sm font-medium text-gray-700">{{ row.name }}</span>
                        <span class="text-sm">
                            <span class="font-bold text-gray-900">{{ fmt(row.registrations) }}</span>
                            <span class="ml-2 text-xs text-gray-400">{{ conv(row.registrations, row.impressions) }}% conv</span>
                        </span>
                    </div>
                    <div class="h-3 rounded-full bg-gray-100 overflow-hidden">
                        <div class="h-full rounded-full bg-brandPrimary transition-all duration-500"
                             :style="{ width: (row.registrations / maxRegistrations * 100) + '%' }"></div>
                    </div>
                </div>
            </div>
        </div>

        <!-- monthly trend vs goal -->
        <div class="grid gap-4 lg:grid-cols-2">
            <div class="p-6 bg-white rounded-lg">
                <h2 class="mb-2 text-xl font-semibold text-gray-800">Impressions vs goal — by month</h2>
                <apexchart type="bar" height="300" :options="impressionsTrendOptions" :series="impressionsTrendSeries" />
            </div>
            <div class="p-6 bg-white rounded-lg">
                <h2 class="mb-2 text-xl font-semibold text-gray-800">New companies vs goal — by month</h2>
                <apexchart type="bar" height="300" :options="companiesTrendOptions" :series="companiesTrendSeries" />
            </div>
        </div>
    </div>
</template>
